import { FilesUploader, If } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { t } from "i18next";
import { ProductsFieldForForms } from "modules/products";
import { useMemo, useState, useCallback } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FormField, MediaFilePicker } from "ui";

import {
  FeedInsertType,
  FeedInsertWidth,
  MAX_MEDIA_FIELDS_IN_INSERT_FORM
} from "../../../../constants";
import { FeedInsertFormValues } from "../FeedInsertForm.types";
import VideoMediaField from "./VideoMediaField";

import styles from "../FeedInsertForm.module.scss";

const IMAGE_CONFIGS = {
  vertical: { width: 175, height: 300, label: "175:300" },
  horizontal: { width: 359, height: 210, label: "359:210" },
  horizontalThin: { width: 359, height: 64, label: "359:64" },
  square: { width: 175, height: 175, label: `175:300 ${t("or")} 359:210` }
};

const InsertMediasField = () => {
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext<FeedInsertFormValues>();

  const {
    fields: mediaFields,
    prepend,
    remove
  } = useFieldArray<FeedInsertFormValues, "media">({
    control,
    name: "media"
  });

  const [videoFiles, setVideoFiles] = useState<Map<number, File>>(new Map());

  const insertType = watch("type");
  const insertForm = watch("form");

  const imageConfig = useMemo(
    () =>
      insertType !== FeedInsertType.SingleMedia
        ? mediaFields[0]
          ? Number(mediaFields[0].media?.height) >
            Number(mediaFields[0].media?.width)
            ? IMAGE_CONFIGS.vertical
            : IMAGE_CONFIGS.horizontal
          : IMAGE_CONFIGS.square
        : insertForm === FeedInsertWidth.FullWidth
          ? IMAGE_CONFIGS.horizontal
          : insertForm === FeedInsertWidth.FullWidthThin
            ? IMAGE_CONFIGS.horizontalThin
            : IMAGE_CONFIGS.vertical,
    [insertType, insertForm, mediaFields[0]]
  );

  const maxFields =
    insertType !== FeedInsertType.SingleMedia
      ? MAX_MEDIA_FIELDS_IN_INSERT_FORM
      : 1;

  const handleVideoFileReceived = useCallback((file: File) => {
    setVideoFiles((prev) => {
      const newMap = new Map(prev);
      newMap.set(0, file);
      return newMap;
    });
  }, []);

  const handleRemoveMedia = useCallback(
    (index: number) => {
      setVideoFiles((prev) => {
        const newMap = new Map(prev);
        newMap.delete(index);
        return newMap;
      });
      remove(index);
    },
    [remove]
  );

  return (
    <FormField label={"Media"} errorMessage={errors.media?.root?.message}>
      <div className={styles.form_mediaFieldsContainer}>
        <If condition={mediaFields.length < maxFields}>
          <FilesUploader
            onSuccessUploading={(media) => {
              prepend({
                media,
                productVariants: [],
                productSet: null,
                allowDisplayInFullScreenMode: true,
                thumbnail: null
              });
            }}
            uploadDirectoryKey={MediaDirectoryKeys.ProductFeedInsert}
            onVideoFileReceived={handleVideoFileReceived}
            additionalValidator={(files) => {
              const allMedias = [
                ...mediaFields.map((media) => media.media),
                ...files
              ];

              const allMediasAreVertical = allMedias.every(
                (file) => Number(file?.height) > Number(file?.width)
              );

              const allMediasAreHorizontal = allMedias.every(
                (file) => Number(file?.width) >= Number(file?.height)
              );

              const isValid = allMediasAreVertical || allMediasAreHorizontal;

              if (!isValid) {
                toast.error(
                  t("All medias in slider should have the same orientation")
                );
              }

              return isValid;
            }}
          >
            {(uploaderProps) => (
              <FormField errorMessage={errors.media?.root?.message}>
                <MediaFilePicker
                  {...uploaderProps}
                  value={null}
                  maxFiles={maxFields - mediaFields.length}
                  customSize={{
                    width: imageConfig.width,
                    height: imageConfig.height
                  }}
                  label={imageConfig.label}
                  onDeleteMediaFile={() => {}}
                />
              </FormField>
            )}
          </FilesUploader>
        </If>

        {mediaFields.length > 0 &&
          (insertType !== FeedInsertType.SingleMedia
            ? mediaFields
            : [mediaFields[0]]
          )?.map((mediaField, index) => {
            const isVideo = mediaField?.media?.mediaType === MediaType.VIDEO;
            return (
              <div
                key={mediaField?.id + index}
                className={styles.form_mediaField}
              >
                <MediaFilePicker
                  onFilesDrop={() => {}}
                  loadingFilesCounter={0}
                  value={mediaField?.media}
                  customSize={{
                    width: imageConfig.width,
                    height: imageConfig.height
                  }}
                  label={imageConfig.label}
                  onDeleteMediaFile={() => handleRemoveMedia(index)}
                />

                <If condition={isVideo}>
                  <VideoMediaField
                    index={index}
                    videoFile={videoFiles.get(index) ?? null}
                    thumbnailSize={{
                      width: imageConfig.width,
                      height: imageConfig.height
                    }}
                  />
                </If>

                <If condition={!isVideo}>
                  <div className={styles.form_mediaFieldContent}>
                    <Controller
                      control={control}
                      name={`media.${index}.productVariants`}
                      render={({
                        field: {
                          value: productsList,
                          onChange: onChangeProductsList
                        }
                      }) => (
                        <Controller
                          control={control}
                          name={`media.${index}.productSet`}
                          render={({
                            field: {
                              value: productSet,
                              onChange: onChangeProductSet
                            }
                          }) => (
                            <ProductsFieldForForms
                              productsList={productsList ?? []}
                              onChangeProductsList={onChangeProductsList}
                              productSet={productSet}
                              onChangeProductSet={onChangeProductSet}
                            />
                          )}
                        />
                      )}
                    />
                  </div>
                </If>
              </div>
            );
          })}
      </div>
    </FormField>
  );
};

export default InsertMediasField;
